import React from 'react';
import { X, FileText } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface ConceptualSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentName: string;
  conceptualSummary: string | null;
}

const ConceptualSummaryModal: React.FC<ConceptualSummaryModalProps> = ({
  isOpen,
  onClose,
  documentName,
  conceptualSummary,
}) => {
  const { theme } = useTheme();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-3xl max-h-[90vh] ${theme.colors.bg.primary} border ${theme.colors.border.primary} rounded-lg shadow-xl flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${theme.colors.border.primary}`}
        >
          <div className="flex items-center gap-3">
            <FileText className={`w-5 h-5 ${theme.colors.text.primary}`} />
            <div>
              <h2 className={`text-lg font-semibold ${theme.colors.text.primary}`}>
                Resumen Conceptual
              </h2>
              <p className={`text-sm ${theme.colors.text.secondary} mt-0.5`}>{documentName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded-lg hover:${theme.colors.bg.secondary} transition-colors ${theme.colors.text.secondary} hover:${theme.colors.text.primary}`}
            title="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {conceptualSummary && conceptualSummary.trim() ? (
            <div
              className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-4`}
            >
              <div
                className={`${theme.colors.text.primary} text-sm leading-relaxed whitespace-pre-wrap break-words`}
                style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
              >
                {conceptualSummary.trim()}
              </div>
            </div>
          ) : (
            <div
              className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-8 text-center`}
            >
              <FileText
                className={`w-12 h-12 ${theme.colors.text.secondary} mx-auto mb-3 opacity-50`}
              />
              <p className={`${theme.colors.text.secondary} text-sm`}>
                Este documento no tiene un resumen conceptual disponible.
              </p>
              <p className={`${theme.colors.text.secondary} text-xs mt-2`}>
                El resumen se genera automáticamente durante el procesamiento del documento.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-end gap-2 px-6 py-3 border-t ${theme.colors.border.primary}`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 ${theme.colors.bg.secondary} hover:${theme.colors.bg.tertiary} ${theme.colors.text.primary} rounded-lg transition-colors text-sm font-medium`}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConceptualSummaryModal;
